import { translate as t } from '@thoughtspot/i18n';
import { SettingsElementType, } from '@thoughtspot/ts-chart-sdk';
var Orientation;
(function (Orientation) {
    Orientation["VERTICAL"] = "Vertical";
    Orientation["HORIZONTAL"] = "Horizontal";
})(Orientation || (Orientation = {}));
var SectionLayout;
(function (SectionLayout) {
    SectionLayout["ACCORDION"] = "accordion";
    SectionLayout["TAB"] = "tab";
    SectionLayout["NONE"] = "none";
})(SectionLayout || (SectionLayout = {}));
var SectionAlignment;
(function (SectionAlignment) {
    SectionAlignment["ROW"] = "row";
    SectionAlignment["COLUMN"] = "column";
})(SectionAlignment || (SectionAlignment = {}));
var PropElementType;
(function (PropElementType) {
    PropElementType["TEXT"] = "text";
    PropElementType["NUMBER"] = "number";
    PropElementType["COLORPICKER"] = "colorpicker";
    PropElementType["TOGGLE"] = "toggle";
    PropElementType["CHECKBOX"] = "checkbox";
    PropElementType["RADIO"] = "radio";
    PropElementType["DROPDOWN"] = "dropdown";
    PropElementType["SECTION"] = "section";
    PropElementType["TOOLTIPCONFIG"] = "tooltipconfig";
})(PropElementType || (PropElementType = {}));
const DEFAULT_TAB_PROPERTIES = {
    layout: 'Horizontal',
    size: 'Small',
    headerAlignment: 'left',
    enableOverflowControls: true,
};
export function isPropElement(element) {
    return element != null && 'type' in element;
}
export function isSettingsElement(element) {
    return element != null && !('type' in element) && 'elementType' in element;
}
const createLabelledView = (element, orientation, children) => {
    if (isPropElement(element)) {
        if (element.type === 'customsort') {
            return null;
        }
        return {
            elementType: SettingsElementType.LABELLED_VIEW,
            properties: {
                label: element.label,
                labelVariant: 'p-small-bold',
                orientation,
            },
            children,
        };
    }
    return {
        elementType: SettingsElementType.LABELLED_VIEW,
        properties: {
            label: element.properties.label,
            labelVariant: 'p-small-bold',
            orientation,
        },
        children,
    };
};
export const createDropdownOptions = (values) => [
    {
        options: values.map(value => {
            let displayValue = '';
            let key = '';
            if (typeof value === 'string') {
                displayValue = value;
                key = value;
            }
            else if (typeof value === 'object') {
                displayValue = value.valueTranslation
                    ? t(value.valueTranslation)
                    : value.value;
                key = value.value;
            }
            return {
                id: key,
                label: displayValue,
            };
        }),
    },
];
const createTabLayout = (element, children) => {
    return {
        key: element.key,
        elementType: SettingsElementType.TAB,
        properties: {
            ...DEFAULT_TAB_PROPERTIES,
            label: element.label,
            disabled: element.disabled,
        },
        children: [
            {
                elementType: SettingsElementType.TAB_ITEM,
                properties: {
                    label: element.label,
                    disabled: element.disabled,
                },
                children,
            },
        ],
    };
};
const elementTransformers = {
    [PropElementType.TEXT]: (element) => {
        const textElement = {
            key: element.key,
            elementType: SettingsElementType.TEXT_INPUT,
            properties: {
                value: element.defaultValue,
                type: element.password ? 'password' : 'text',
                disabled: element.disabled,
                placeholder: element.placeholder,
                multiline: element.multiline,
            },
        };
        return createLabelledView(element, Orientation.VERTICAL, [textElement]);
    },
    [PropElementType.NUMBER]: (element) => {
        const numberElement = {
            key: element.key,
            elementType: SettingsElementType.NUMBER_INPUT,
            properties: {
                value: element.defaultValue,
                disabled: element.disabled,
            },
        };
        return createLabelledView(element, Orientation.HORIZONTAL, [
            numberElement,
        ]);
    },
    [PropElementType.COLORPICKER]: (element) => {
        const colorElement = {
            key: element.key,
            elementType: SettingsElementType.COLOR_PICKER,
            properties: {
                color: element.defaultValue,
            },
        };
        return createLabelledView(element, Orientation.HORIZONTAL, [
            colorElement,
        ]);
    },
    [PropElementType.TOGGLE]: (element) => {
        const toggleElement = {
            key: element.key,
            elementType: SettingsElementType.TOGGLE,
            properties: {
                value: element.defaultValue,
                disabled: element.disabled,
            },
        };
        return createLabelledView(element, Orientation.HORIZONTAL, [
            toggleElement,
        ]);
    },
    [PropElementType.CHECKBOX]: (element) => {
        const checkboxElement = {
            key: element.key,
            elementType: SettingsElementType.CHECKBOX,
            properties: {
                value: element.defaultValue,
                disabled: element.disabled,
                label: element.label,
            },
        };
        return checkboxElement;
    },
    [PropElementType.RADIO]: (element) => {
        const radioElement = {
            key: element.key,
            elementType: SettingsElementType.RADIO_GROUP,
            properties: {
                value: element.defaultValue,
                disabled: element.disabled,
            },
            children: element.values.map(value => {
                let displayValue = '';
                let key = '';
                if (typeof value === 'string') {
                    displayValue = value;
                    key = value;
                }
                else if (typeof value === 'object') {
                    displayValue = value.valueTranslation
                        ? t(value.valueTranslation)
                        : value.value;
                    key = value.value;
                }
                return {
                    elementType: SettingsElementType.RADIO,
                    properties: {
                        key,
                        label: displayValue,
                    },
                };
            }),
        };
        return createLabelledView(element, Orientation.VERTICAL, [
            radioElement,
        ]);
    },
    [PropElementType.DROPDOWN]: (element) => {
        const dropdownElement = {
            key: element.key,
            elementType: SettingsElementType.DROPDOWN,
            properties: {
                value: element.defaultValue,
                optionsGroups: createDropdownOptions(element.values),
                disabled: element.disabled,
            },
        };
        return createLabelledView(element, Orientation.VERTICAL, [
            dropdownElement,
        ]);
    },
    [PropElementType.TOOLTIPCONFIG]: (element) => {
        return {
            key: element.key,
            elementType: SettingsElementType.TOOLTIP_CONFIGURATOR,
            properties: {
                value: element.defaultValue,
                disabled: element.disabled,
                label: element.label,
            },
        };
    },
    [PropElementType.SECTION]: (element, transformer) => {
        const processChildren = (children) => {
            return children
                ?.map(child => {
                if (child.type === PropElementType.SECTION) {
                    const innerSection = child;
                    return {
                        elementType: SettingsElementType.LABELLED_VIEW,
                        properties: {
                            label: innerSection.label,
                            orientation: Orientation.VERTICAL,
                        },
                        children: processChildren(innerSection.children || []),
                    };
                }
                return transformer(child);
            })
                .filter(Boolean);
        };
        const childContent = processChildren(element.children || []);
        if (element.alignment === SectionAlignment.COLUMN) {
            if (element.layoutType === SectionLayout.TAB) {
                return createTabLayout(element, childContent);
            }
        }
        return {
            key: element.key,
            elementType: SettingsElementType.SECTION,
            properties: {
                label: element.label,
                disabled: element.disabled,
                alignment: element.alignment,
            },
            children: childContent,
        };
    },
};
export const transformPropElementToChartSettings = (propElement) => {
    if (!propElement)
        return null;
    if (isPropElement(propElement)) {
        switch (propElement.type) {
            case PropElementType.SECTION:
                return elementTransformers[PropElementType.SECTION](propElement, transformPropElementToChartSettings);
            case PropElementType.TEXT:
                return elementTransformers[PropElementType.TEXT](propElement);
            case PropElementType.NUMBER:
                return elementTransformers[PropElementType.NUMBER](propElement);
            case PropElementType.COLORPICKER:
                return elementTransformers[PropElementType.COLORPICKER](propElement);
            case PropElementType.TOGGLE:
                return elementTransformers[PropElementType.TOGGLE](propElement);
            case PropElementType.CHECKBOX:
                return elementTransformers[PropElementType.CHECKBOX](propElement);
            case PropElementType.RADIO:
                return elementTransformers[PropElementType.RADIO](propElement);
            case PropElementType.DROPDOWN:
                return elementTransformers[PropElementType.DROPDOWN](propElement);
            case PropElementType.TOOLTIPCONFIG:
                return elementTransformers[PropElementType.TOOLTIPCONFIG](propElement);
            default:
                return null;
        }
    }
    return propElement;
};
export function getIsChartSettingsV2EnabledValue(appConfig) {
    return appConfig?.initFlags?.isChartSettingsV2Enabled?.flagValue ?? false;
}
//# sourceMappingURL=custom-chart-schema-hydrator.util.js.map